/**
 * OmegaSpecialistPage — L2 specialist detail at `/omega/specialist/:specialistId`.
 *
 * Shows:
 *   1. Breadcrumb: OMEGA > Specialists > {specialistId}
 *   2. Header with status badge (sourced from brain summaries)
 *   3. Execution History — last 20 log entries where payload_json.specialist_id matches
 *   4. Attached Triggers — listTriggers() filtered by specialist_id
 */

import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { useBrainSummaries } from '@/features/omega/dashboard/hooks/useOmegaState';
import { getOmegaLogTail } from '@/api/real/omegaLog';
import { listTriggers } from '@/api/real/triggers';
import type { OmegaLogEntry, TriggerSummary, TriggerKind } from '@/api/types';
import { cn } from '@/utils/cn';

const PAGE_SIZE = 20;

export function OmegaSpecialistPage() {
  const { specialistId } = useParams<{ specialistId: string }>();

  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <SpecialistPageInner specialistId={specialistId ?? ''} />;
}

function SpecialistPageInner({ specialistId }: { specialistId: string }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data: brains } = useBrainSummaries();

  const logQuery = useQuery({
    queryKey: ['omega', 'log', 'specialist', specialistId],
    queryFn: () => getOmegaLogTail(undefined, 100),
    refetchInterval: 10_000,
    select: (res) =>
      res.entries.filter(
        (e) => (e.payload_json as Record<string, unknown>)?.specialist_id === specialistId,
      ),
  });

  const triggersQuery = useQuery({
    queryKey: ['triggers', 'list'],
    queryFn: listTriggers,
    refetchInterval: 30_000,
    select: (all) => all.filter((t) => t.specialist_id === specialistId),
  });

  // Resolve status from any brain that has this specialist bound.
  const specialistStatus: string = (() => {
    if (!brains) return 'unknown';
    for (const brain of brains) {
      const found = brain.bound_specialists.find((s) => s.id === specialistId);
      if (found) return found.status;
    }
    return 'unknown';
  })();

  const allEntries: OmegaLogEntry[] = logQuery.data ?? [];
  const visibleEntries = allEntries.slice(0, visibleCount);
  const hasMore = allEntries.length > visibleCount;

  const attachedTriggers: TriggerSummary[] = triggersQuery.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Specialists' },
          { label: specialistId },
        ]}
      />

      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h1 className="ltr-content text-2xl font-semibold tracking-tight text-ink">
            {specialistId}
          </h1>
          <SpecialistStatusChip status={specialistStatus} />
        </div>
      </header>

      <ExecutionHistorySection
        entries={visibleEntries}
        isPending={logQuery.isPending}
        isError={logQuery.isError}
        hasMore={hasMore}
        onLoadMore={() => setVisibleCount((n) => n + PAGE_SIZE)}
      />

      <AttachedTriggersSection
        triggers={attachedTriggers}
        isPending={triggersQuery.isPending}
        isError={triggersQuery.isError}
      />
    </div>
  );
}

// ── Execution History ─────────────────────────────────────────────────────────

interface ExecutionHistorySectionProps {
  entries: OmegaLogEntry[];
  isPending: boolean;
  isError: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

function ExecutionHistorySection({
  entries,
  isPending,
  isError,
  hasMore,
  onLoadMore,
}: ExecutionHistorySectionProps) {
  return (
    <section
      aria-labelledby="exec-history-heading"
      className="rounded-xl border border-border bg-surface p-6"
    >
      <h2 id="exec-history-heading" className="text-lg font-semibold text-ink">
        Execution History
      </h2>

      {isPending && <p className="mt-3 text-sm text-ink-muted">Loading…</p>}

      {isError && (
        <p className="mt-3 text-sm text-danger">Failed to load log entries.</p>
      )}

      {!isPending && !isError && entries.length === 0 && (
        <p className="mt-3 text-sm text-ink-muted">
          No executions recorded for this specialist yet.
        </p>
      )}

      {!isPending && !isError && entries.length > 0 && (
        <>
          <div className="mt-5 overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                  <th className="px-3 py-2 text-start font-medium">Time</th>
                  <th className="px-3 py-2 text-start font-medium">Entry Type</th>
                  <th className="px-3 py-2 text-start font-medium">Status</th>
                  <th className="px-3 py-2 text-start font-medium">Seq</th>
                  <th className="px-3 py-2 text-start font-medium">Payload</th>
                  <th className="px-3 py-2 text-start font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <LogEntryRow key={entry.seq} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={onLoadMore}
              className="mt-4 text-sm text-ink-muted hover:text-ink underline"
            >
              Load 20 more
            </button>
          )}
        </>
      )}
    </section>
  );
}

function LogEntryRow({ entry }: { entry: OmegaLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  const payloadStr =
    entry.payload_json !== null && entry.payload_json !== undefined
      ? JSON.stringify(entry.payload_json)
      : '';
  const payloadSummary = payloadStr.length > 60 ? payloadStr.slice(0, 60) + '…' : payloadStr;

  const statusIcon = entryStatusIcon(entry.entry_type);
  const timestamp = entryTimestamp(entry);

  return (
    <tr className="border-t border-border hover:bg-surface-raised/50">
      <td className="px-3 py-2 text-ink-muted whitespace-nowrap">{timestamp}</td>
      <td className="px-3 py-2 font-mono text-xs text-ink">{entry.entry_type}</td>
      <td className="px-3 py-2 text-center">{statusIcon}</td>
      <td className="px-3 py-2 font-mono text-xs text-ink-muted">{entry.seq}</td>
      <td className="px-3 py-2 max-w-xs truncate">
        <span className="ltr-content font-mono text-xs text-ink-muted">{payloadSummary}</span>
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="rounded border border-border px-2 py-0.5 text-xs text-ink-muted hover:text-ink hover:border-spark"
        >
          {expanded ? 'Hide' : 'View'}
        </button>
        {expanded && (
          <pre className="mt-2 max-w-sm whitespace-pre-wrap break-all rounded bg-surface-raised p-2 text-[11px] text-ink ltr-content">
            {JSON.stringify(entry.payload_json, null, 2)}
          </pre>
        )}
      </td>
    </tr>
  );
}

function entryStatusIcon(entryType: string): string {
  if (entryType.includes('error') || entryType.includes('refused')) return '✗';
  if (entryType.includes('fired') || entryType.includes('exec')) return '✓';
  return '·';
}

function entryTimestamp(entry: OmegaLogEntry): string {
  const payload = entry.payload_json as Record<string, unknown> | null;
  const iso =
    typeof payload?.fired_at === 'string'
      ? payload.fired_at
      : typeof payload?.created_at === 'string'
        ? payload.created_at
        : null;
  if (!iso) return `seq ${entry.seq}`;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ── Attached Triggers ─────────────────────────────────────────────────────────

interface AttachedTriggersSectionProps {
  triggers: TriggerSummary[];
  isPending: boolean;
  isError: boolean;
}

function AttachedTriggersSection({
  triggers,
  isPending,
  isError,
}: AttachedTriggersSectionProps) {
  return (
    <section
      aria-labelledby="triggers-heading"
      className="rounded-xl border border-border bg-surface p-6"
    >
      <h2 id="triggers-heading" className="text-lg font-semibold text-ink">
        Attached Triggers
      </h2>

      {isPending && <p className="mt-3 text-sm text-ink-muted">Loading…</p>}

      {isError && (
        <p className="mt-3 text-sm text-danger">Failed to load triggers.</p>
      )}

      {!isPending && !isError && triggers.length === 0 && (
        <p className="mt-3 text-sm text-ink-muted">No triggers attached to this specialist.</p>
      )}

      {!isPending && !isError && triggers.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                <th className="px-3 py-2 text-start font-medium">Trigger ID</th>
                <th className="px-3 py-2 text-start font-medium">Type</th>
                <th className="px-3 py-2 text-start font-medium">Enabled</th>
                <th className="px-3 py-2 text-start font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {triggers.map((t) => (
                <tr key={t.trigger_id} className="border-t border-border hover:bg-surface-raised/50">
                  <td className="px-3 py-2 font-mono text-xs text-ink ltr-content">
                    {t.trigger_id}
                  </td>
                  <td className="px-3 py-2">
                    <TriggerKindBadge kind={t.trigger_type} />
                  </td>
                  <td className="px-3 py-2">
                    <EnabledBadge enabled={t.enabled} />
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/omega/triggers"
                      className="text-xs text-ink-muted hover:text-ink hover:underline"
                    >
                      Go to Triggers →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Shared chips ──────────────────────────────────────────────────────────────

function SpecialistStatusChip({ status }: { status: string }) {
  const tone =
    status === 'live'
      ? 'border-success/30 bg-success-soft text-success'
      : status === 'blueprint'
        ? 'border-border bg-surface-raised text-ink-muted'
        : 'border-border bg-surface-raised text-ink-muted';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider',
        tone,
      )}
    >
      {status}
    </span>
  );
}

function TriggerKindBadge({ kind }: { kind: TriggerKind }) {
  const label = kind.kind;
  return (
    <span className="inline-flex items-center rounded border border-border px-2 py-0.5 font-mono text-[11px] text-ink-muted">
      {label}
    </span>
  );
}

function EnabledBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider',
        enabled
          ? 'border-success/30 bg-success-soft text-success'
          : 'border-border bg-surface-raised text-ink-muted',
      )}
    >
      {enabled ? 'enabled' : 'disabled'}
    </span>
  );
}

export default OmegaSpecialistPage;
